import axios from 'axios';
import React, { useEffect, useState, useRef, useContext } from 'react';
import { MarktingContext } from '../../Context/MarktingContext';
import css from "./style.module.css";
import { useNavigate } from 'react-router-dom';

export default function ConvertedHtml({
  id,
  comments,
  setActiveComment,
  loginToken,
  archived,
  setDocumentStatus,
  activeCommentFromFeed // New prop for eye icon functionality
}) {
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const previousActiveElementRef = useRef(null); // Track previous DOM element
  const commentElementRefs = useRef({}); // New ref to store comment element references
  const localCommentsRef = useRef([]); // A ref to always track the latest localComments value
  const timeoutRef = useRef(null); // For highlight timeout
  const { setMarktingData, marktingData, archivedData } = useContext(MarktingContext);

  const [content, setContent] = useState('');
  const [activeElementId, setActiveElementId] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [inputPosition, setInputPosition] = useState({ x: 0, y: 0 });
  const [formPosition, setFormPosition] = useState({ top: 0, left: 0 });
  const [localComments, setLocalComments] = useState([]);
  const [activeParentid, setActiveParentid] = useState(null);
  const [highlightedCommentId, setHighlightedCommentId] = useState(null); // New state for highlighting
  const navigation = useNavigate();

  // Update localComments from props when available
  useEffect(() => {
    if (comments) {
      setLocalComments(comments);
    }
  }, [comments]);

  useEffect(() => {
    localCommentsRef.current = localComments;
  }, [localComments]);

  // New effect to handle activeCommentFromFeed changes
  useEffect(() => {
    // Clear previous highlight and timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (activeCommentFromFeed && activeCommentFromFeed.elementId && iframeRef.current) {
      const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;

      // Remove previous highlights from all elements
      Object.values(commentElementRefs.current).forEach(element => {
        if (element) {
          const svgElement = element.querySelector('svg circle');
          if (svgElement) {
            svgElement.style.filter = '';
            svgElement.style.transform = '';
            svgElement.style.fill = 'var(--ink-soft)'; // Reset to default color
          }
          // Remove any existing pulse rings
          const existingPulse = element.querySelector('.pulse-ring');
          if (existingPulse) {
            existingPulse.remove();
          }
        }
      });

      const commentElement = commentElementRefs.current[activeCommentFromFeed.elementId];

      if (commentElement) {
        // Scroll the comment into view within the iframe
        commentElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        // Highlight the comment
        setHighlightedCommentId(activeCommentFromFeed.elementId);

        // Apply highlight styling to the comment circle
        const svgElement = commentElement.querySelector('svg circle');
        if (svgElement) {
          svgElement.style.fill = 'var(--accent)'; // Change fill color
          svgElement.style.filter = 'drop-shadow(0 0 10px rgba(195, 29, 138, 0.8))';
          svgElement.style.transform = 'scale(1.2)';
          svgElement.style.transition = 'all 0.3s ease';
        }

        // Add pulse ring effect
        const pulseRing = iframeDoc.createElement("div");
        pulseRing.style.position = "absolute";
        pulseRing.style.left = "50%";
        pulseRing.style.top = "50%";
        pulseRing.style.transform = "translate(-50%, -50%)";
        pulseRing.style.width = "60px";
        pulseRing.style.height = "60px";
        pulseRing.style.borderRadius = "50%";
        pulseRing.style.border = "2px solid var(--accent)";
        pulseRing.style.animation = "pulse 2s infinite";
        pulseRing.style.pointerEvents = "none";
        pulseRing.className = "pulse-ring";
        commentElement.appendChild(pulseRing);

        // Remove highlight after 3 seconds
        timeoutRef.current = setTimeout(() => {
          setHighlightedCommentId(null);
          if (svgElement) {
            svgElement.style.fill = 'var(--ink-soft)'; // Reset fill color
            svgElement.style.filter = '';
            svgElement.style.transform = '';
          }
          // Remove pulse ring
          const existingPulse = commentElement.querySelector('.pulse-ring');
          if (existingPulse) {
            existingPulse.remove();
          }
        }, 3000);
      }
    }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [activeCommentFromFeed]);

  const calculateFormPosition = (clickEvent, clickedElement) => {
    const formWidth = 320;
    const formHeight = 200; // Approximate height of the form
    const padding = 10;

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Get the container element (iframe container or main container)
    const container = containerRef.current ||
      clickEvent.currentTarget.closest('.overflow-auto') ||
      clickEvent.currentTarget.closest('[style*="overflowY: auto"]');
    const containerRect = container ? container.getBoundingClientRect() : {
      top: 0,
      left: 0,
      right: viewportWidth,
      bottom: viewportHeight
    };

    // Get click position relative to viewport
    let x = clickEvent.clientX;
    let y = clickEvent.clientY;

    // Calculate preferred position (to the right and below the click)
    let left = x + padding;
    let top = y + padding;

    // Adjust horizontal position if form would go outside viewport or container
    if (left + formWidth > Math.min(viewportWidth, containerRect.right) - padding) {
      left = x - formWidth - padding; // Position to the left of click
    }
    if (left < Math.max(0, containerRect.left) + padding) {
      left = Math.max(0, containerRect.left) + padding; // Align to left edge with padding
    }

    // Adjust vertical position if form would go outside viewport or container
    if (top + formHeight > Math.min(viewportHeight, containerRect.bottom) - padding) {
      top = Math.max(y - formHeight - padding, Math.max(0, containerRect.top) + padding); // Position above click or at top
    }
    if (top < Math.max(0, containerRect.top) + padding) {
      top = Math.max(0, containerRect.top) + padding; // Align to top edge with padding
    }

    // Ensure the form stays within the container bounds
    left = Math.max(containerRect.left + padding, Math.min(left, containerRect.right - formWidth - padding));
    top = Math.max(containerRect.top + padding, Math.min(top, containerRect.bottom - formHeight - padding));

    return { top, left };
  };

  function findParentWithHexId(element) {
    const hexRegex = /^pf[0-9a-fA-F]+$/;
    let current = element.parentElement;
    while (current) {
      if (hexRegex.test(current.id)) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  // Fetch and prepare content from remote endpoint
  useEffect(() => {
    const controller = new AbortController();
    const fetchContent = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/content/${id}`,
          {
            headers: {
              Authorization: `Bearer ${loginToken}`,
            },
            signal: controller.signal
          }
        );
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = res?.data?.fileContent;
        setContent(tempDiv.innerHTML);
      } catch (error) {
        if (error?.response?.status === 401) {
          localStorage.removeItem("Firebase Auth");
          localStorage.removeItem("okta-token-storage");
          localStorage.removeItem("okta-cache-storage");
          navigation('/');
          return;
        };
        if (error.name !== 'AbortError') {
          // console.error('Error fetching content:', error);
        }
      }
    };
    fetchContent();
    return () => controller.abort();
  }, [id, loginToken, navigation]);

  // Write content into the iframe and attach click handler
  useEffect(() => {
    if (content && iframeRef.current) {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

      iframeDoc.open();
      iframeDoc.write(content);
      iframeDoc.close();

      // Attach click handler to every element inside the iframe once loaded
      iframe.onload = () => {
        // Always attach click handlers even for archived content to allow viewing comments
        iframeDoc.querySelectorAll('*').forEach((element) => {
          element.onclick = handleClick;
        });
      };
    }
  }, [content]);

  // Process comments from appropriate data source based on archived status
  useEffect(() => {
    if (!content || !iframeRef.current) return;
    const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
    let fetchedComments = [];

    // Use the appropriate data source based on archived status
    const dataSource = archived ? archivedData : marktingData;

    const element = dataSource?.find((item) =>
      item.documentId === id || item.version?.some(version => version.versionId === id)
    );

    if (element) {
      fetchedComments = element.documentId === id
        ? element.Comments ?? []
        : element.version.find(version => version.versionId === id)?.Comments ?? [];
    }

    setLocalComments(fetchedComments);
    let counter = 0;

    // Clear previous comment element references
    commentElementRefs.current = {};

    // Remove all existing comment icons first
    const existingIcons = iframeDoc.querySelectorAll('[style*="z-index: 9999999"]');
    existingIcons.forEach(icon => icon.remove());

    fetchedComments.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).forEach(comment => {
      const el = iframeDoc.getElementById(comment.parentId) || iframeDoc.getElementById(comment.elementId);
      if (el) {
        if (comment.created_by !== "PIRM AI Bot") {
          counter++;
        }

        const iconEl = iframeDoc.createElement("div");
        iconEl.style.position = "absolute";
        iconEl.style.left = comment.XLine + "px";
        iconEl.style.top = comment.YLine + "px";
        iconEl.style.margin = "0px";
        iconEl.style.transform = "translate(-50%, -50%)";
        iconEl.style.zIndex = "9999999";
        iconEl.style.cursor = "pointer";

        // Store reference to this comment element
        commentElementRefs.current[comment.elementId] = iconEl;

        // Determine fill color based on highlight status
        const isHighlighted = highlightedCommentId === comment.elementId;
        const fillColor = isHighlighted ? 'var(--accent)' : 'var(--ink-soft)';

        const svgHTML = comment.created_by === "PIRM AI Bot" ? ""
          : `<svg width="35" height="35" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
  <!-- Comment circle -->
  <circle id=${comment.elementId} cx="100" cy="100" r="100" fill="${fillColor}" style="transition: all 0.3s ease;" />
  <!-- Centered number -->
  <text 
    id=${comment.elementId}
    x="50%" 
    y="50%" 
    fill="white" 
    font-family="Arial, sans-serif" 
    font-size="80" 
    text-anchor="middle" 
    dominant-baseline="central">
    ${counter}
  </text>
</svg>
`;

        iconEl.innerHTML = svgHTML;

        // Apply additional effects if this comment is currently highlighted
        if (isHighlighted) {
          const svgCircle = iconEl.querySelector('svg circle');
          if (svgCircle) {
            svgCircle.style.fill = 'var(--accent)';
            svgCircle.style.filter = 'drop-shadow(0 0 10px rgba(98, 195, 29, 0.8))';
            svgCircle.style.transform = 'scale(1.2)';
          }

          // Add pulse ring
          const pulseRing = iframeDoc.createElement("div");
          pulseRing.style.position = "absolute";
          pulseRing.style.left = "50%";
          pulseRing.style.top = "50%";
          pulseRing.style.transform = "translate(-50%, -50%)";
          pulseRing.style.width = "60px";
          pulseRing.style.height = "60px";
          pulseRing.style.borderRadius = "50%";
          pulseRing.style.border = "2px solid var(--accent)";
          pulseRing.style.animation = "pulse 2s infinite";
          pulseRing.style.pointerEvents = "none";
          pulseRing.className = "pulse-ring";
          iconEl.appendChild(pulseRing);
        }

        el.appendChild(iconEl);
      }
    });

    // Add CSS animation to iframe document
    if (iframeDoc.head && !iframeDoc.getElementById('comment-animations')) {
      const style = iframeDoc.createElement('style');
      style.id = 'comment-animations';
      style.textContent = `
        @keyframes pulse {
          0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          50% { transform: translate(-50%, -50%) scale(1.5); opacity: 0.5; }
          100% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
        }
      `;
      iframeDoc.head.appendChild(style);
    }
  }, [content, marktingData, archivedData, id, archived, highlightedCommentId]);

  // Click handler - can still click to view comments when archived, but not add new ones
  const handleClick = (e) => {    
    let clickedElement = e.target;
    const parentElement = clickedElement.previousElementSibling;
    if (parentElement?.id === clickedElement?.id) {
      clickedElement = parentElement;
    }

    setActiveComment({ elementId: clickedElement?.id });

    // Check if this is a comment circle
    const isExistingComment = localCommentsRef.current.some(
      c => c.elementId === clickedElement.id
    );

    // If archived, only allow clicking on existing comments
    if (archived && !isExistingComment) {
      return;
    }

    const parent = findParentWithHexId(clickedElement);

    if (parent) {
      let formattedId;
      do {
        // Generate a 3-digit random number between 100 and 999
        const randomId = Math.floor(Math.random() * 900) + 100;
        formattedId = String(randomId);
      } while (
        localCommentsRef.current.some(
          comment => comment.elementId === `${clickedElement.id}_p${formattedId}`
        )
      );
      setActiveParentid(parent.id);
      setActiveElementId(`${parent.id}${formattedId}`);

      const parentRect = parent.getBoundingClientRect();
      const relativeX = e.clientX - parentRect.left;
      const relativeY = e.clientY - parentRect.top;
      setInputPosition({ x: relativeX, y: relativeY });

      // Calculate form position using the new function
      if (!archived) {
        const calculatedPosition = calculateFormPosition(e, clickedElement);
        setFormPosition(calculatedPosition);
      }
    }

    // Handle SVG fill changes on click (similar to your existing logic)
    if (previousActiveElementRef.current) {
      // Reset previous element fill
      if (previousActiveElementRef.current.tagName === 'circle' || previousActiveElementRef.current.tagName === 'text') {
        previousActiveElementRef.current.style.fill = 'var(--ink-soft)';
      }
      
      const isComment = localCommentsRef.current.some(
        c => c.elementId === previousActiveElementRef.current.id
      );

      if (isComment && clickedElement) {
        setActiveElementId(null);
        // Set new element fill if it's an SVG element
        if (clickedElement.tagName === 'circle' || clickedElement.tagName === 'text') {
          clickedElement.style.fill = 'var(--accent)';
        }
      }
    }
    previousActiveElementRef.current = clickedElement;
    setCommentText('');
  };

  const handleSave = async () => {
    if (!activeElementId || !previousActiveElementRef.current) return;

    // Don't allow saving comments if the content is archived
    if (archived) {
      return;
    }

    const element =
      iframeRef.current.contentDocument.getElementById(activeElementId) ||
      iframeRef.current.contentDocument.getElementById(activeParentid);
    if (!element) return;

    const payload = {
      parentId: activeParentid,
      elementId: activeElementId,
      comment: commentText.trim(),
      XLine: inputPosition.x || 0,
      YLine: inputPosition.y || 0,
    };

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/comment`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${loginToken}`,
          },
          params: { id },// Placed inside the same config object
        }
      );

      if (data?.data && data?.status) {
        // Update local comments immediately using the functional update
        setLocalComments(prevComments => [...prevComments, data.data]);

        const deepClone = JSON.parse(JSON.stringify(marktingData));
        const elementToUpdate = deepClone.find(
          item => item.documentId === id || item.version?.some(version => version.versionId === id)
        );
        if (!elementToUpdate) return;
        if (elementToUpdate.documentId === id) {
          elementToUpdate.Comments = elementToUpdate.Comments || [];
          elementToUpdate.Comments.push(data.data);
          elementToUpdate.status = data.status;
        } else {
          const version = elementToUpdate.version.find(v => v.versionId === id);
          version.Comments = version.Comments || [];
          version.Comments.push(data.data);
          version.status = data.status;
        }
        setDocumentStatus(data.status);
        setMarktingData(deepClone);
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        localStorage.removeItem("Firebase Auth");
        localStorage.removeItem("okta-token-storage");
        localStorage.removeItem("okta-cache-storage");
        navigation('/');
        return;
      };
      // console.error("Error saving comment:", error);
    }

    if (previousActiveElementRef.current) {
      const isImageComment =
        previousActiveElementRef.current.tagName === "P" &&
        previousActiveElementRef.current.id.includes("image");
      const isExistingComment = localCommentsRef.current.some(
        c => c.elementId === previousActiveElementRef.current.id
      );
      if (!isExistingComment) {
        if (isImageComment) {
          previousActiveElementRef.current.style.color = '';
        } else {
          previousActiveElementRef.current.style.outline = '';
        }
      }
    }

    setActiveElementId(null);
    setCommentText('');
  };

  const handleCancel = () => {
    if (!activeElementId) return;
    const savedComment = comments?.find(
      comment => comment.elementId === activeElementId
    );
    if (!savedComment) {
      previousActiveElementRef.current.style.outline = '';
      previousActiveElementRef.current = null;
    }
    setActiveElementId(null);
    setCommentText('');
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <iframe
        ref={iframeRef}
        id="myIframe"
        title="content-frame"
        className="w-100 vh-100"
      />
      {activeElementId && !archived && (
        <div
          className="form-control"
          style={{
            position: 'fixed',
            top: `${formPosition.top}px`,
            left: `${formPosition.left}px`,
            padding: '10px',
            border: '1px solid #ccc',
            zIndex: 1000,
            width: '320px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
            backgroundColor: 'var(--surface)',
          }}
        >
          <textarea
            value={commentText}
            onChange={e => setCommentText(e.target.value)}
            placeholder="Enter your comment here..."
            style={{
              width: '100%',
              height: '130px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '8px',
              resize: 'none'
            }}
            autoFocus
          />
          <div className="d-flex gap-2 mt-1">
            <button
              className="b-pink-color flex-grow-1"
              onClick={e => {
                e.stopPropagation();
                handleSave();
              }}
            >
              Save
            </button>
            <button
              className="btn btn-outline-danger"
              onClick={e => {
                e.stopPropagation();
                handleCancel();
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}